import { ReactNode, useContext, useEffect, useId, useState } from "react";
import { ButtonBase, Link, SvgIcon } from "@mui/material";
import { InstituteRule } from "@verity/api-client/types";
import { ThemeWrapper } from "@verity/components";
import {
	CoreButton,
	CoreCheckbox,
	CoreErrorMessage,
	CoreRadioGroup,
	CoreTable,
	CoreTextField,
	coreToast,
	RadioOption,
} from "@verity/components/core";
import { filterDefined } from "../../../../helpers";
import apiClient from "../../../../helpers/api-client";
import {
	courseIdToCourseUrl,
	courseUrlToCourseId,
	determineCourseInputType,
	validateCourseId,
} from "../../../../helpers/scope";
import TextSkeleton from "../../../../utility/TextSkeleton";
import { PortalControllerContext } from "../../../context";
import i18n from "../../../../i18n";
import { CustomSettingData } from "../../settings-page";
import styles from "./index.module.css";
import CaretRight from "../../../../images/portal/caret-right.svg?react";

const PROCTORING_METHODS = ["automated", "live", "pop-in", "pop-in-post-review"] as const;
type ProctoringMethod = (typeof PROCTORING_METHODS)[number];
type Scope = (typeof SCOPE_ARRAY)[number];
const SCOPE_ARRAY = ["global", "lmsProvidedCourseId", "courseNamePrefix"] as const;
type LmsProvidedCourseIdSettings = { ids: string[] };
type CourseNamePrefixSettings = { prefixes: string[] };
type ScopeSettings = {
	global: Record<string, never>;
	lmsProvidedCourseId: LmsProvidedCourseIdSettings;
	courseNamePrefix: CourseNamePrefixSettings;
};
type MethodScopeSettings = {
	selectedScope: Scope;
	scopeSettings: ScopeSettings;
};
export type InstituteMethodScopeSetting = Record<ProctoringMethod, MethodScopeSettings>;

type NewInstituteRule = Omit<InstituteRule, "type">;

export async function getInstituteMethodScopeSettings(instituteUuid?: string): Promise<InstituteMethodScopeSetting> {
	if (instituteUuid) {
		const rules = (await apiClient.institute.getInstituteRules(instituteUuid)) as (InstituteRule & {
			details: { option: string; value: ProctoringMethod; enabled: boolean };
		})[];
		return Object.fromEntries(
			PROCTORING_METHODS.map((method): [ProctoringMethod, MethodScopeSettings] => {
				const methodRules = rules.filter(
					({ details }) => details.option === "proctoringMethod" && details.value === method,
				);
				const enablingRules = methodRules.filter(({ details }) => details.enabled);
				const scopeSettings: ScopeSettings = {
					global: {},
					lmsProvidedCourseId: {
						ids: filterDefined(
							enablingRules.map((rule) => (rule.scope === "lmsProvidedCourseId" ? rule.scopeValue : undefined)),
						),
					},
					courseNamePrefix: {
						prefixes: filterDefined(
							enablingRules.map((rule) => (rule.scope === "courseNamePrefix" ? rule.scopeValue : undefined)),
						),
					},
				};
				const enabledRules = methodRules.filter(({ enabled }) => enabled);
				if (enabledRules.length === 0) {
					return [method, { selectedScope: "global", scopeSettings }];
				}
				const enabledEnablingRules = enabledRules.filter(({ details }) => details.enabled);
				if (enabledEnablingRules.length === 0) {
					return [method, { selectedScope: "lmsProvidedCourseId", scopeSettings }];
				}
				const [firstRule] = enabledEnablingRules;
				switch (firstRule.scope) {
					case "global":
						return [method, { selectedScope: "global", scopeSettings }];
					case "lmsProvidedCourseId":
						return [
							method,
							{
								selectedScope: "lmsProvidedCourseId",
								scopeSettings,
							},
						];
					case "courseNamePrefix":
						return [
							method,
							{
								selectedScope: "courseNamePrefix",
								scopeSettings,
							},
						];
				}
			}),
		) as InstituteMethodScopeSetting;
	} else {
		return Object.fromEntries(
			PROCTORING_METHODS.map((method): [ProctoringMethod, MethodScopeSettings] => [
				method,
				{
					selectedScope: "global",
					scopeSettings: { global: {}, lmsProvidedCourseId: { ids: [] }, courseNamePrefix: { prefixes: [] } },
				},
			]),
		) as InstituteMethodScopeSetting;
	}
}

function generateLmsProvidedCourseIdRules(
	lmsProvidedCourseIdSettings: LmsProvidedCourseIdSettings,
	method: ProctoringMethod,
): Omit<NewInstituteRule, "enabled">[] {
	return lmsProvidedCourseIdSettings.ids.map((id) => ({
		scope: "lmsProvidedCourseId",
		scopeValue: id,
		details: { option: "proctoringMethod", value: method, enabled: true },
	}));
}

function generateCourseNamePrefixRules(
	courseNamePrefixSettings: CourseNamePrefixSettings,
	method: ProctoringMethod,
): Omit<NewInstituteRule, "enabled">[] {
	return courseNamePrefixSettings.prefixes.map((prefix) => ({
		scope: "courseNamePrefix",
		scopeValue: prefix,
		details: { option: "proctoringMethod", value: method, enabled: true },
	}));
}

function addRuleEnabled(rules: Omit<NewInstituteRule, "enabled">[], enabled: boolean): NewInstituteRule[] {
	return rules.map((rule) => ({ ...rule, enabled }));
}

export async function saveInstituteMethodScopeSettings(instituteUuid?: string, value?: InstituteMethodScopeSetting) {
	if (!value) {
		return { ok: true };
	}

	try {
		if (instituteUuid) {
			const rules: NewInstituteRule[] = Object.entries(value).flatMap(([method, settings]) => {
				const lmsProvidedCourseIdRules = generateLmsProvidedCourseIdRules(
					settings.scopeSettings.lmsProvidedCourseId,
					method as ProctoringMethod,
				);
				const courseNamePrefixRules = generateCourseNamePrefixRules(
					settings.scopeSettings.courseNamePrefix,
					method as ProctoringMethod,
				);
				const globalRuleDetails = { option: "proctoringMethod", value: method, enabled: true };
				if (settings.selectedScope === "global") {
					return [
						{
							scope: "global",
							details: globalRuleDetails,
							enabled: true,
						},
						...addRuleEnabled(lmsProvidedCourseIdRules, false),
						...addRuleEnabled(courseNamePrefixRules, false),
					];
				} else {
					const methodRules: NewInstituteRule[] = [
						{
							scope: "global",
							details: { ...globalRuleDetails, enabled: false },
							enabled: true,
						},
					];
					if (settings.selectedScope === "lmsProvidedCourseId") {
						return [
							...methodRules,
							...addRuleEnabled(lmsProvidedCourseIdRules, true),
							...addRuleEnabled(courseNamePrefixRules, false),
						];
					} else {
						return [
							...methodRules,
							...addRuleEnabled(courseNamePrefixRules, true),
							...addRuleEnabled(lmsProvidedCourseIdRules, false),
						];
					}
				}
			});
			await apiClient.institute.updateInstituteRules({
				instituteUuid,
				type: "editConfiguration",
				rules: rules,
			});
		} else {
			throw new Error("no instituteId");
		}
		return { ok: true };
	} catch (e) {
		console.error(e);
		return { ok: false, reason: "Failed to save methods setting." };
	}
}

function TextScopeInput({
	value,
	setValue,
	onSubmit,
	label,
	placeholder,
	error,
}: {
	value: string;
	setValue: (newValue: string) => void;
	onSubmit: () => void;
	label: ReactNode;
	placeholder: string;
	error?: string;
}) {
	const inputId = useId();
	return (
		<form
			className={styles.textScopeInput}
			onSubmit={(e) => {
				e.preventDefault();
				onSubmit();
			}}
		>
			<label htmlFor={inputId}>{label}</label>
			<div className={styles.inputRow}>
				<CoreTextField
					id={inputId}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					label={placeholder}
					error={!!error}
					sx={{ flex: 1 }}
				/>
				<CoreButton type="submit" className={styles.addButton}>
					{i18n.t("portal.components.settings.instituteMethodScopeSettings.add")}
				</CoreButton>
			</div>
			{error && <CoreErrorMessage>{error}</CoreErrorMessage>}
		</form>
	);
}

function LmsProvidedCourseIdSettings({
	settings,
	onChange,
}: {
	settings: LmsProvidedCourseIdSettings;
	onChange: (newSettings: LmsProvidedCourseIdSettings) => void;
}) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms } = portalContext;

	const [inputValue, setInputValue] = useState("");

	const [selectedIds, setSelectedIds] = useState(new Set<string>());
	useEffect(() => {
		const existingIds = new Set(settings.ids);
		setSelectedIds(new Set([...selectedIds].filter((id) => existingIds.has(id))));
	}, [settings.ids]);

	const tryExtractCourseId = (): [undefined, string] | [string, undefined] => {
		if (!lms) {
			return [undefined, i18n.t("portal.components.settingsPageTSX.defaultError")];
		}

		const containsUrlComponents = determineCourseInputType(inputValue) === "url";

		/* Attempt to generate a valid (HTTPS) URL if the request contains url components */
		let url: URL | null = null;
		if (containsUrlComponents) {
			try {
				url = new URL(inputValue);

				if (url.protocol !== "https:") {
					return [undefined, i18n.t("portal.components.settingsPageTSX.invalidProtocol")];
				}
			} catch (e) {
				return [undefined, i18n.t("portal.components.settingsPageTSX.invalidUrl")];
			}
		}

		/* Extract the course ID */
		const courseId = url ? courseUrlToCourseId(lms, url) : inputValue;
		if (!courseId) {
			return [undefined, i18n.t("portal.components.settingsPageTSX.invalidCourse")];
		}

		/* Validate the course ID */
		const isValidCourseId = validateCourseId(lms, courseId);
		if (!isValidCourseId) {
			return [undefined, i18n.t("portal.components.settingsPageTSX.invalidCourse")];
		}

		/* Detect duplicates */
		if (settings.ids.includes(courseId)) {
			return [undefined, i18n.t("portal.components.settingsPageTSX.duplicateCourse")];
		}
		return [courseId, undefined];
	};

	const getInputError = () => {
		if (!lms || !inputValue) {
			return undefined;
		}
		try {
			return tryExtractCourseId()[1];
		} catch (e) {
			console.error(e);
			return i18n.t("portal.components.settingsPageTSX.invalidUrl");
		}
	};

	const allEntriesSelected = selectedIds.size > 0 && settings.ids.every((id) => selectedIds.has(id));

	const inputError = getInputError();

	return (
		<div className={styles.scopeSettingsBody}>
			<TextScopeInput
				value={inputValue}
				setValue={setInputValue}
				onSubmit={() => {
					if (!lms) {
						return coreToast.error(i18n.t("portal.components.settingsPageTSX.defaultError"));
					}
					if (!inputValue) {
						return;
					}
					try {
						const [courseId] = tryExtractCourseId();
						if (courseId) {
							onChange({ ...settings, ids: settings.ids.concat([courseId]) });
							setInputValue("");
						}
					} catch (e) {
						console.error(e);
						return coreToast.error(i18n.t("portal.components.settingsPageTSX.defaultError"));
					}
				}}
				label={i18n.t("portal.components.settings.instituteMethodScopeSettings.lmsProvidedCourseId.addCourse")}
				placeholder={i18n.t("portal.components.settings.instituteMethodScopeSettings.lmsProvidedCourseId.enterCourse")}
				error={inputError}
			/>
			<div className={styles.textScopeTableWrapper}>
				{selectedIds.size > 0 && (
					<CoreButton
						color="cancel"
						className={styles.deleteButton}
						onClick={() =>
							onChange({
								ids: settings.ids.filter((id) => !selectedIds.has(id)),
							})
						}
					>
						{i18n.t("portal.components.settings.instituteMethodScopeSettings.delete")}
					</CoreButton>
				)}
				<div>
					<CoreTable
						hidePaginationOnSinglePage
						paginationSize="small"
						initialSortKey="courseId"
						initialSortOrder="asc"
						sortRequired
						columns={[
							{
								key: "select",
								header: (
									<CoreCheckbox
										checked={allEntriesSelected}
										indeterminate={selectedIds.size > 0 && !allEntriesSelected}
										onChange={(e) => {
											if (e.target.checked) {
												setSelectedIds(new Set(settings.ids));
											} else {
												setSelectedIds(new Set());
											}
										}}
									/>
								),
								cell: (row) => (
									<CoreCheckbox
										checked={selectedIds.has(row.data.id)}
										onChange={(e) => {
											const newSelectedIds = new Set(selectedIds);
											if (e.target.checked) {
												newSelectedIds.add(row.data.id);
											} else {
												newSelectedIds.delete(row.data.id);
											}
											setSelectedIds(newSelectedIds);
										}}
									/>
								),
								width: "50px",
							},
							{
								key: "courseId",
								header: i18n.t("portal.components.settings.instituteMethodScopeSettings.lmsProvidedCourseId.courseId"),
								cell: (row) => row.data.id,
								descendingComparator: (a, b) => a.id.localeCompare(b.id),
							},
							{
								key: "courseUrl",
								header: i18n.t("portal.components.settings.instituteMethodScopeSettings.lmsProvidedCourseId.courseUrl"),
								cell: (row) => {
									const courseUrl = courseIdToCourseUrl(portalContext, row.data.id);
									if (courseUrl) {
										return (
											<Link href={courseUrl} target="_blank">
												{courseUrl}
											</Link>
										);
									}
									return null;
								},
							},
						]}
						data={settings.ids.map((id) => ({ id }))}
						noResultsTitle={i18n.t(
							"portal.components.settings.instituteMethodScopeSettings.lmsProvidedCourseId.noResults.title",
						)}
						noResultsString={i18n.t(
							"portal.components.settings.instituteMethodScopeSettings.lmsProvidedCourseId.noResults.description",
						)}
					/>
				</div>
			</div>
		</div>
	);
}

function PrefixCourseCount({ prefix }: { prefix: string }) {
	const { portalContext } = useContext(PortalControllerContext);

	const [count, setCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		if (portalContext.instituteUuid) {
			apiClient.course
				.getCourseCountForPrefix(portalContext.instituteUuid!, prefix)
				.then((courses) => setCount(courses))
				.catch((e) => {
					console.error(e);
					setError(true);
				})
				.finally(() => setLoading(false));
		} else {
			setError(true);
			setLoading(false);
		}
	}, []);

	if (loading) {
		return <TextSkeleton />;
	}
	return error ? null : count;
}

function CourseNamePrefixSettings({
	settings,
	onChange,
}: {
	settings: CourseNamePrefixSettings;
	onChange: (newSettings: CourseNamePrefixSettings) => void;
}) {
	const [inputValue, setInputValue] = useState("");

	const [selectedPrefixes, setSelectedPrefixes] = useState(new Set<string>());

	useEffect(() => {
		const existingPrefixes = new Set(settings.prefixes);
		setSelectedPrefixes(new Set([...selectedPrefixes].filter((prefix) => existingPrefixes.has(prefix))));
	}, [settings.prefixes]);

	const allEntriesSelected =
		selectedPrefixes.size > 0 && settings.prefixes.every((prefix) => selectedPrefixes.has(prefix));

	return (
		<div className={styles.scopeSettingsBody}>
			<TextScopeInput
				value={inputValue}
				setValue={setInputValue}
				onSubmit={() => {
					if (inputValue) {
						onChange({ ...settings, prefixes: settings.prefixes.concat([inputValue]) });
						setInputValue("");
					}
				}}
				label={i18n.t("portal.components.settings.instituteMethodScopeSettings.courseNamePrefix.addCoursePrefix")}
				placeholder={i18n.t(
					"portal.components.settings.instituteMethodScopeSettings.courseNamePrefix.enterCoursePrefix",
				)}
			/>
			<div className={styles.textScopeTableWrapper}>
				{selectedPrefixes.size > 0 && (
					<CoreButton
						color="cancel"
						className={styles.deleteButton}
						onClick={() =>
							onChange({
								prefixes: settings.prefixes.filter((prefix) => !selectedPrefixes.has(prefix)),
							})
						}
					>
						{i18n.t("portal.components.settings.instituteMethodScopeSettings.delete")}
					</CoreButton>
				)}
				<div>
					<CoreTable
						hidePaginationOnSinglePage
						paginationSize="small"
						initialSortKey="courseId"
						initialSortOrder="asc"
						sortRequired
						columns={[
							{
								key: "select",
								header: (
									<CoreCheckbox
										checked={allEntriesSelected}
										indeterminate={selectedPrefixes.size > 0 && !allEntriesSelected}
										onChange={(e) => {
											if (e.target.checked) {
												setSelectedPrefixes(new Set(settings.prefixes));
											} else {
												setSelectedPrefixes(new Set());
											}
										}}
									/>
								),
								cell: (row) => (
									<CoreCheckbox
										checked={selectedPrefixes.has(row.data.prefix)}
										onChange={(e) => {
											const newSelectedPrefixes = new Set(selectedPrefixes);
											if (e.target.checked) {
												newSelectedPrefixes.add(row.data.prefix);
											} else {
												newSelectedPrefixes.delete(row.data.prefix);
											}
											setSelectedPrefixes(newSelectedPrefixes);
										}}
									/>
								),
								width: "50px",
							},
							{
								key: "prefix",
								header: i18n.t("portal.components.settings.instituteMethodScopeSettings.courseNamePrefix.coursePrefix"),
								cell: (row) => row.data.prefix,
								descendingComparator: (a, b) => a.prefix.localeCompare(b.prefix),
							},
							{
								key: "count",
								header: i18n.t("portal.components.settings.instituteMethodScopeSettings.courseNamePrefix.count"),
								cell: (row) => <PrefixCourseCount prefix={row.data.prefix} />,
								descendingComparator: (a, b) => a.prefix.localeCompare(b.prefix),
							},
						]}
						data={settings.prefixes.map((prefix) => ({ prefix }))}
						noResultsTitle={i18n.t(
							"portal.components.settings.instituteMethodScopeSettings.courseNamePrefix.noResults.title",
						)}
						noResultsString={i18n.t(
							"portal.components.settings.instituteMethodScopeSettings.courseNamePrefix.noResults.description",
						)}
					/>
				</div>
			</div>
		</div>
	);
}

function MethodScopeSettingsCard({
	method,
	settings,
	onChange,
}: {
	method: ProctoringMethod;
	settings: MethodScopeSettings;
	onChange: (newSettings: MethodScopeSettings) => void;
}) {
	const [expanded, setExpanded] = useState(false);
	const { selectedScope } = settings;

	const onScopeSettingsChange = (newScopeSettings: MethodScopeSettings["scopeSettings"]) =>
		onChange({
			...settings,
			scopeSettings: newScopeSettings,
		});

	const expandButtonId = useId();
	const bodyId = useId();
	const headerText = i18n.t(`portal.components.settings.instituteMethodScopeSettings.method.${method}`);

	const radioOptions: RadioOption[] = SCOPE_ARRAY.map((scope) => ({
		value: scope,
		label: i18n.t(`portal.components.settings.instituteMethodScopeSettings.${scope}.option`),
	}));

	return (
		<div className={`${styles.methodCard} ${expanded ? styles.expanded : ""}`}>
			<ButtonBase
				id={expandButtonId}
				disableRipple
				className={styles.methodCardHeader}
				onClick={() => setExpanded(!expanded)}
				aria-expanded={expanded}
				aria-controls={bodyId}
			>
				{headerText}
				<SvgIcon className={styles.expandIcon}>
					<CaretRight />
				</SvgIcon>
			</ButtonBase>
			<div id={bodyId} role="region" className={styles.methodCardBody} aria-labelledby={expandButtonId}>
				<CoreRadioGroup
					radioOptions={radioOptions}
					radioLabelFontSize={15}
					onChange={(_, value) => onChange({ ...settings, selectedScope: value as Scope })}
					selectedValue={selectedScope}
					radioGroupAriaLabel={i18n.t("portal.components.settings.instituteMethodScopeSettings.radioGroup.ariaLabel", {
						scope: headerText,
					})}
				/>
				{selectedScope === "lmsProvidedCourseId" && (
					<LmsProvidedCourseIdSettings
						settings={settings.scopeSettings.lmsProvidedCourseId}
						onChange={(newSettings) =>
							onScopeSettingsChange({
								...settings.scopeSettings,
								lmsProvidedCourseId: newSettings,
							})
						}
					/>
				)}
				{selectedScope === "courseNamePrefix" && (
					<CourseNamePrefixSettings
						settings={settings.scopeSettings.courseNamePrefix}
						onChange={(newSettings) =>
							onScopeSettingsChange({
								...settings.scopeSettings,
								courseNamePrefix: newSettings,
							})
						}
					/>
				)}
			</div>
		</div>
	);
}

export default function InstituteMethodScopeSettings({
	loadData,
	setting,
	onSettingChange,
}: {
	loadData: InstituteMethodScopeSetting;
	setting: CustomSettingData<InstituteMethodScopeSetting>;
	onSettingChange: (value: InstituteMethodScopeSetting) => void;
}) {
	const { verityInstituteSettings } = useContext(PortalControllerContext);
	const { allowLiveProctoring, allowLivePopIn, allowLivePopInPostReview } = verityInstituteSettings;

	const [currSettings, setCurrSettings] = useState(setting?.value ?? loadData);
	const updateSettings = (settings: InstituteMethodScopeSetting) => {
		setCurrSettings(settings);
		onSettingChange(settings);
	};

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.settings }}>
			{i18n.t("portal.components.settings.instituteMethodScopeSettings.description")}
			<div className={styles.methods}>
				<MethodScopeSettingsCard
					method="automated"
					settings={currSettings.automated}
					onChange={(newSettings) =>
						updateSettings({
							...currSettings,
							automated: newSettings,
						})
					}
				/>
				{allowLiveProctoring && (
					<MethodScopeSettingsCard
						method="live"
						settings={currSettings.live}
						onChange={(newSettings) =>
							updateSettings({
								...currSettings,
								live: newSettings,
							})
						}
					/>
				)}
				{allowLivePopIn && (
					<MethodScopeSettingsCard
						method="pop-in"
						settings={currSettings["pop-in"]}
						onChange={(newSettings) =>
							updateSettings({
								...currSettings,
								"pop-in": newSettings,
							})
						}
					/>
				)}
				{allowLivePopInPostReview && (
					<MethodScopeSettingsCard
						method="pop-in-post-review"
						settings={currSettings["pop-in-post-review"]}
						onChange={(newSettings) =>
							updateSettings({
								...currSettings,
								"pop-in-post-review": newSettings,
							})
						}
					/>
				)}
			</div>
		</ThemeWrapper>
	);
}
